/**
 * DemoPanel — Sales Demonstration Environment control (admin-only).
 *
 * Shows the seeded demo dataset status, a one-click Reset Demo (wipe + re-seed),
 * the fixed demo logins to hand to prospects, and captured "Request a Demo"
 * leads. Backend is gated by DEMO_MODE_ENABLED; this tab is gated by
 * REACT_APP_DEMO_MODE. Read/seed only — never touches the live dispatch core.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Presentation, RotateCcw, RefreshCw, Copy, Mail, Users, Car, Building2,
  ShieldCheck, AlertTriangle, CheckCircle2, Monitor, Send, Loader2, Info,
} from "lucide-react";

const DEMO_LOGINS = [
  { role: "Admin / Dispatcher", email: "demo.admin@crystaldispatch.demo", icon: ShieldCheck },
  { role: "Driver", email: "demo.driver1@crystaldispatch.demo", icon: Car },
  { role: "Passenger", email: "demo.pax1@crystaldispatch.demo", icon: Users },
  { role: "Corporate Admin", email: "demo.corporate@crystaldispatch.demo", icon: Building2 },
];

export const DemoPanel = ({
  kioskEnabled, setKioskEnabled, kioskTimeoutMin, setKioskTimeoutMin, kioskReset, setKioskReset,
}) => {
  const [status, setStatus] = useState(null);
  const [requests, setRequests] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState({});   // `${id}:${which}` -> true
  const [detailOpen, setDetailOpen] = useState({});  // `${id}:${which}` -> true

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.allSettled([
        api.get("/admin/demo/status"),
        api.get("/admin/demo-requests"),
      ]);
      if (s.status === "fulfilled") setStatus(s.value.data);
      if (r.status === "fulfilled") setRequests(r.value.data.requests || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resend = useCallback(async (leadId, which) => {
    const key = `${leadId}:${which}`;
    setResending((m) => ({ ...m, [key]: true }));
    try {
      const path = which === "confirmation"
        ? `/admin/demo-requests/${leadId}/resend-confirmation`
        : `/admin/demo-requests/${leadId}/resend-alert`;
      const { data } = await api.post(path);
      if (data.status === "sent") {
        toast.success(which === "confirmation" ? "Confirmation re-sent to the prospect." : "Admin alert re-sent.");
      } else {
        toast.error(`Send ${data.status || "failed"}${data.error ? `: ${data.error}` : ""}`);
      }
      await load();
    } catch (e) {
      toast.error(`Resend failed: ${formatApiError(e)}`);
    } finally {
      setResending((m) => { const n = { ...m }; delete n[key]; return n; });
    }
  }, [load]);

  const resetDemo = useCallback(async () => {
    if (!window.confirm("Reset the demo environment? This wipes all demo data and re-seeds a fresh dataset. Takes ~30 seconds. Real accounts are never touched.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/demo/reset");
      toast.success(`Demo reset — ${data.created.rides} rides, ${data.created.drivers} drivers, ${data.created.passengers} passengers seeded.`);
      await load();
    } catch (e) {
      toast.error(`Reset failed: ${formatApiError(e)}`);
    } finally {
      setResetting(false);
    }
  }, [load]);

  const copy = (text) => {
    try { navigator.clipboard?.writeText(text)?.catch(() => {}); } catch (_) { /* clipboard unavailable */ }
    toast.success("Copied");
  };

  return (
    <div className="space-y-4" data-testid="demo-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 grid place-items-center bg-[#6D28D9] text-white rounded-md">
            <Presentation className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-black tracking-tight">Sales Demonstration Environment</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              A fully-seeded, resettable demo of a live dispatch operation.
            </p>
          </div>
        </div>
        <button
          onClick={resetDemo}
          disabled={resetting}
          data-testid="demo-reset-btn"
          className="px-3.5 py-2 rounded-md bg-[#6D28D9] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
        >
          <RotateCcw className={`w-4 h-4 ${resetting ? "animate-spin" : ""}`} />
          {resetting ? "Resetting…" : "Reset Demo"}
        </button>
      </div>

      {resetting && (
        <div className="flex items-center gap-2 p-3 border border-dashed rounded-md text-sm text-muted-foreground" data-testid="demo-resetting">
          <RefreshCw className="w-4 h-4 animate-spin text-[#6D28D9]" />
          Rebuilding the demo dataset… this takes about 30 seconds.
        </div>
      )}

      {/* Status counts */}
      <div className="border border-border rounded-md bg-card">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
          <span className="font-semibold text-sm">Seeded data</span>
          <button onClick={load} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="demo-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <div className="p-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="demo-counts">
          {status && Object.entries(status.counts).map(([k, v]) => (
            <div key={k} className="text-center">
              <div className="text-xl font-black tracking-tight">{v}</div>
              <div className="label-mini text-muted-foreground capitalize">{k.replace(/_/g, " ")}</div>
            </div>
          ))}
        </div>
        {status && (
          <div className="px-4 py-2 border-t border-border text-[11px] flex items-center gap-1.5">
            {status.total > 0
              ? <><CheckCircle2 className="w-3.5 h-3.5 text-[#16A34A]" /> <span className="text-muted-foreground">{status.total} demo records ready · login password <code className="font-mono-tab">{status.demo_password}</code></span></>
              : <><AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B]" /> <span className="text-muted-foreground">No demo data yet — click <strong>Reset Demo</strong> to seed it.</span></>}
          </div>
        )}
      </div>

      {/* Kiosk / Exhibition mode */}
      <div className="border border-border rounded-md bg-card" data-testid="kiosk-controls">
        <div className="px-4 py-2.5 border-b border-border font-semibold text-sm flex items-center gap-2">
          <Monitor className="w-4 h-4 text-[#6D28D9]" /> Exhibition / Kiosk Mode
        </div>
        <div className="p-4 space-y-3">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm">
              <span className="font-medium">Enable Kiosk Mode</span>
              <span className="block text-[11px] text-muted-foreground">After inactivity, auto-return to the welcome selector for the next visitor.</span>
            </span>
            <input
              type="checkbox" checked={!!kioskEnabled}
              onChange={(e) => setKioskEnabled(e.target.checked)}
              className="w-4 h-4 accent-[#6D28D9]" data-testid="kiosk-enable-toggle"
            />
          </label>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">Inactivity timeout</span>
            <select
              value={kioskTimeoutMin}
              onChange={(e) => setKioskTimeoutMin(Number(e.target.value))}
              disabled={!kioskEnabled}
              className="text-sm border border-input rounded-md px-2 py-1.5 disabled:opacity-50 bg-background"
              data-testid="kiosk-timeout-select"
            >
              <option value={3}>3 minutes</option>
              <option value={4}>4 minutes</option>
              <option value={5}>5 minutes</option>
            </select>
          </div>
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm">
              <span className="font-medium">Reset demo data on idle</span>
              <span className="block text-[11px] text-muted-foreground">Re-seed a fresh dataset each time (adds ~30s; leave off for faster turnover).</span>
            </span>
            <input
              type="checkbox" checked={!!kioskReset}
              onChange={(e) => setKioskReset(e.target.checked)}
              disabled={!kioskEnabled}
              className="w-4 h-4 accent-[#6D28D9] disabled:opacity-50" data-testid="kiosk-reset-toggle"
            />
          </label>
          {kioskEnabled && (
            <div className="text-[11px] text-[#15803D] flex items-center gap-1.5" data-testid="kiosk-active-note">
              <CheckCircle2 className="w-3.5 h-3.5" /> Kiosk active — returns to the welcome screen after {kioskTimeoutMin} min idle{kioskReset ? " and re-seeds demo data" : ""}.
            </div>
          )}
        </div>
      </div>

      {/* Demo logins */}
      <div className="border border-border rounded-md bg-card">
        <div className="px-4 py-2.5 border-b border-border font-semibold text-sm">Demo logins (password: <code className="font-mono-tab">demo123</code>)</div>
        <div className="divide-y divide-border">
          {DEMO_LOGINS.map((l) => {
            const Icon = l.icon;
            return (
              <div key={l.email} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`demo-login-${l.role.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-4 h-4 text-[#6D28D9] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{l.role}</div>
                    <div className="text-[11px] text-muted-foreground font-mono-tab truncate">{l.email}</div>
                  </div>
                </div>
                <button onClick={() => copy(l.email)} className="text-muted-foreground hover:text-foreground" title="Copy email">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Demo requests (leads) */}
      <div className="border border-border rounded-md bg-card">
        <div className="px-4 py-2.5 border-b border-border font-semibold text-sm flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#6D28D9]" /> Demo requests ({requests.length})
        </div>
        {requests.length === 0 ? (
          <div className="px-4 py-4 text-xs text-muted-foreground" data-testid="demo-requests-empty">
            No demo requests yet. Leads from the public “Request a Demo” form appear here.
          </div>
        ) : (
          <div className="divide-y divide-border max-h-96 overflow-auto" data-testid="demo-requests-list">
            {requests.map((r) => {
              const es = r.email_status || {};
              return (
                <div key={r.id} className="px-4 py-3" data-testid={`demo-lead-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{r.name}{r.company ? ` · ${r.company}` : ""}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono-tab">{r.email}{r.phone ? ` · ${r.phone}` : ""}{r.role ? ` · ${r.role}` : ""}</div>
                  {r.message && <div className="text-[11px] text-foreground/80 mt-1">{r.message}</div>}
                  <div className="mt-2 flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">Admin alert</span>
                      <EmailStatusPill status={es.admin_alert?.status} testid={`lead-${r.id}-alert-status`} />
                      {es.admin_alert?.status === "sent" && es.admin_alert?.provider_id && (
                        <button onClick={() => setDetailOpen((m) => ({ ...m, [`${r.id}:alert`]: !m[`${r.id}:alert`] }))}
                          className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                          data-testid={`lead-${r.id}-alert-details`}>
                          <Info className="w-3 h-3" /> Details
                        </button>
                      )}
                      <button
                        onClick={() => resend(r.id, "alert")}
                        disabled={!!resending[`${r.id}:alert`]}
                        data-testid={`lead-${r.id}-resend-alert`}
                        className="inline-flex items-center gap-1 text-[10px] text-[#6D28D9] hover:underline disabled:opacity-50"
                      >
                        {resending[`${r.id}:alert`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend
                      </button>
                    </div>
                    {detailOpen[`${r.id}:alert`] && <SentMeta meta={es.admin_alert} copy={copy} testid={`lead-${r.id}-alert-meta`} />}
                    {es.admin_alert?.reason && (
                      <div className="text-[10px] text-[#991B1B] break-words pl-[2px]" data-testid={`lead-${r.id}-alert-reason`}>
                        Reason: {es.admin_alert.reason}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">Confirmation</span>
                      <EmailStatusPill status={es.confirmation?.status} testid={`lead-${r.id}-confirmation-status`} />
                      {es.confirmation?.status === "sent" && es.confirmation?.provider_id && (
                        <button onClick={() => setDetailOpen((m) => ({ ...m, [`${r.id}:conf`]: !m[`${r.id}:conf`] }))}
                          className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                          data-testid={`lead-${r.id}-confirmation-details`}>
                          <Info className="w-3 h-3" /> Details
                        </button>
                      )}
                      <button
                        onClick={() => resend(r.id, "confirmation")}
                        disabled={!!resending[`${r.id}:confirmation`]}
                        data-testid={`lead-${r.id}-resend-confirmation`}
                        className="inline-flex items-center gap-1 text-[10px] text-[#6D28D9] hover:underline disabled:opacity-50"
                      >
                        {resending[`${r.id}:confirmation`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend
                      </button>
                    </div>
                    {detailOpen[`${r.id}:conf`] && <SentMeta meta={es.confirmation} copy={copy} testid={`lead-${r.id}-confirmation-meta`} />}
                    {es.confirmation?.reason && (
                      <div className="text-[10px] text-[#991B1B] break-words pl-[2px]" data-testid={`lead-${r.id}-confirmation-reason`}>
                        Reason: {es.confirmation.reason}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const EMAIL_STATUS = {
  sent: { label: "Sent", cls: "border-[#10B981] text-[#065F46] bg-[#ECFDF5]" },
  failed: { label: "Failed", cls: "border-[#EF4444] text-[#991B1B] bg-[#FEE2E2]" },
  pending: { label: "Pending", cls: "border-[#F59E0B] text-[#92400E] bg-[#FEF3C7]" },
};

const EmailStatusPill = ({ status, testid }) => {
  const cfg = EMAIL_STATUS[status] || EMAIL_STATUS.pending;
  return <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 border rounded-sm ${cfg.cls}`} data-testid={testid}>{cfg.label}</span>;
};

const SentMeta = ({ meta, copy, testid }) => {
  if (!meta?.provider_id) return null;
  return (
    <div className="ml-1 pl-2 border-l-2 border-border text-[10px] text-muted-foreground space-y-0.5 py-0.5" data-testid={testid}>
      <div className="flex items-center gap-1">
        <span className="uppercase tracking-wide text-[9px]">Message ID</span>
        <code className="font-mono-tab text-foreground/80 break-all" data-testid={`${testid}-id`}>{meta.provider_id}</code>
        <button onClick={() => copy(meta.provider_id)} className="text-muted-foreground hover:text-foreground flex-shrink-0" title="Copy message ID for the Resend dashboard" data-testid={`${testid}-copy`}>
          <Copy className="w-3 h-3" />
        </button>
      </div>
      {meta.to && <div>To: <span className="font-mono-tab text-foreground/80">{meta.to}</span></div>}
      {meta.at && <div>Sent: {new Date(meta.at).toLocaleString("en-GB")}</div>}
    </div>
  );
};

export default DemoPanel;